import { motion } from "framer-motion";
import { Sparkles, Compass, Award } from "lucide-react";
import { BIRDS } from "@/lib/brand";
import { useLang } from "@/lib/i18n";

export default function Manifesto() {
  const { t } = useLang();
  const CHAPTERS = [
    {
      n: "01",
      title: t("manifesto.m1_title"),
      body: t("manifesto.m1_body"),
      icon: Award,
      bird: BIRDS.bird_pop,
    },
    {
      n: "02",
      title: t("manifesto.m2_title"),
      body: t("manifesto.m2_body"),
      icon: Sparkles,
      bird: BIRDS.bird_soar,
    },
    {
      n: "03",
      title: t("manifesto.m3_title"),
      body: t("manifesto.m3_body"),
      icon: Compass,
      bird: BIRDS.bird_side,
    },
  ];
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative bg-[#F4F4F0] text-black border-t border-black"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-black" />
              <span className="font-mono tracking-[0.22em] uppercase">
                {t("manifesto.chapter")}
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.98] tracking-tight">
              {t("manifesto.title_1")} <br />
              <em className="italic">{t("manifesto.title_2_italic")}</em> <br />
              {t("manifesto.title_3")}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 0.9 }}
              className="font-sans font-medium text-xl md:text-2xl leading-relaxed max-w-[42ch]"
              data-testid="manifesto-philosophy"
            >
              “{t("manifesto.philosophy")}”
            </motion.p>
            <div className="mt-5 font-mono tracking-[0.2em] uppercase text-black/65">
              {t("manifesto.philosophy_by")}
            </div>
          </div>
        </div>

        <div className="border-t border-black">
          {CHAPTERS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className="grid grid-cols-12 gap-6 md:gap-10 border-b border-black py-10 md:py-14 group"
                data-testid={`manifesto-chapter-${c.n}`}
              >
                <div className="col-span-3 md:col-span-2">
                  <div className="font-display text-6xl md:text-8xl leading-none text-black/20 group-hover:text-[color:var(--ap-magenta)] transition-colors duration-500">
                    {c.n}
                  </div>
                </div>
                <div className="col-span-9 md:col-span-6">
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4" />
                    <div className="font-sans text-xs md:text-sm font-semibold tracking-[0.16em] uppercase">
                      {t("manifesto.mission_point")}
                    </div>
                  </div>
                  <h3 className="font-display text-3xl md:text-5xl mt-3 leading-[1.08]">
                    {c.title}.
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-4 flex md:items-start gap-4">
                  <img
                    src={c.bird}
                    alt=""
                    aria-hidden
                    className="w-14 h-14 md:w-16 md:h-16 object-contain shrink-0 mt-1 opacity-95 transition-transform duration-700 group-hover:rotate-[6deg]"
                  />
                  <p className="text-base md:text-lg text-black/80 leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
